import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TIPUS_ZONA, useDesa, useEtag, useZones, type ZonaAdminDto } from '@/api/administracio';
import { Desplegable, type OpcioDesplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit, Camp } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { Calaix } from './marc/Calaix';
import { Cerca, ErrorDesa } from './comu';
import { arbreZones, descendents } from './sistema/utils';
import './administracio.css';
import './sistema.css';

const normalitza = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

function EditaZona({
  inicial,
  zones,
  onTanca,
}: {
  inicial: ZonaAdminDto;
  zones: ZonaAdminDto[];
  onTanca: () => void;
}) {
  const { t } = useTranslation('administracio');
  const { etag } = useEtag('/zones', inicial.id);
  const desa = useDesa<ZonaAdminDto>('/zones', ['zones'], etag);
  const [z, setZ] = useState(inicial);
  const nova = inicial.id == null;
  const pareInicial = zones.find((x) => x.id === inicial.pareId);

  // La zona superior no pot ser la mateixa zona ni cap dels seus descendents (evita cicles).
  const opcionsPare = useMemo<OpcioDesplegable<number | ''>[]>(() => {
    const prohibides = inicial.id != null ? descendents(zones, inicial.id) : new Set<number>();
    if (inicial.id != null) prohibides.add(inicial.id);
    return [
      { valor: '', etiqueta: t('zones.sensePare') },
      ...arbreZones(zones)
        .filter(({ zona }) => zona.id != null && !prohibides.has(zona.id))
        .map(({ zona, nivell }) => ({
          valor: zona.id as number,
          etiqueta: zona.nom,
          detall: `${t(`zones.tipusZona.${zona.tipus}`)} · ${zona.codi}`,
          nivell,
        })),
    ];
  }, [zones, inicial.id, t]);

  return (
    <Calaix
      titol={nova ? t('zones.nova') : t('zones.editaZona', { nom: inicial.nom })}
      etiqueta={nova ? t('sistema.zones.novaEtiqueta') : t('zones.zona')}
      on={
        nova && pareInicial ? (
          <>
            {t('sistema.zones.dins')} <strong>{pareInicial.nom}</strong>
          </>
        ) : undefined
      }
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('comu.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--dark btn--s"
            disabled={!z.codi.trim() || !z.nom.trim() || desa.isPending}
            onClick={() => desa.mutate(z, { onSuccess: onTanca })}
          >
            <Icona nom="checkmark" />
            {nova ? t('sistema.zones.crea') : t('comu.desa')}
          </button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 12 }}>
        <Camp etiqueta={t('zones.codi')}>
          <input
            className="input mono"
            value={z.codi}
            disabled={!nova}
            onChange={(e) => setZ({ ...z, codi: e.target.value.toUpperCase() })}
          />
        </Camp>
        <Camp etiqueta={t('zones.nom')}>
          <input
            className="input"
            value={z.nom}
            onChange={(e) => setZ({ ...z, nom: e.target.value })}
          />
        </Camp>
      </div>
      <p className="camp__ajuda" style={{ margin: '-6px 0 0' }}>
        {nova ? t('sistema.zones.codiFix') : t('sistema.zones.codiBloquejat')}
      </p>
      <Camp etiqueta={t('zones.tipus')}>
        <Desplegable
          aria-label={t('zones.tipus')}
          valor={z.tipus}
          opcions={TIPUS_ZONA.map((tp) => ({ valor: tp, etiqueta: t(`zones.tipusZona.${tp}`) }))}
          onCanvia={(tipus) => setZ({ ...z, tipus })}
        />
      </Camp>
      <Camp etiqueta={t('zones.adreca')}>
        <input
          className="input"
          value={z.adreca ?? ''}
          onChange={(e) => setZ({ ...z, adreca: e.target.value || null })}
        />
      </Camp>
      <Camp etiqueta={t('zones.pare')}>
        <Desplegable
          aria-label={t('zones.pare')}
          valor={z.pareId ?? ''}
          opcions={opcionsPare}
          onCanvia={(v) => setZ({ ...z, pareId: v === '' ? null : v })}
        />
      </Camp>
      <label className="switch-row">
        <span>
          {t('comu.actiu')}
          <span className="camp__ajuda" style={{ display: 'block' }}>
            {t('sistema.zones.ajudaActiu')}
          </span>
        </span>
        <input
          type="checkbox"
          className="switch"
          checked={z.actiu}
          onChange={(e) => setZ({ ...z, actiu: e.target.checked })}
        />
      </label>
      <ErrorDesa error={desa.error} />
    </Calaix>
  );
}

export function W28Zones() {
  const { t } = useTranslation('administracio');
  const { pot } = useMe();
  const zones = useZones();
  const [edita, setEdita] = useState<ZonaAdminDto | null>(null);
  const [text, setText] = useState('');
  const [inactius, setInactius] = useState(false);
  const [plegades, setPlegades] = useState<Set<number>>(new Set());
  const totes = useMemo(() => zones.data ?? [], [zones.data]);
  const potAlta = pot('cai.zones', 'ALTA');
  const potModificar = pot('cai.zones', 'MODIFICAR');
  const q = normalitza(text.trim());
  const nInactives = totes.filter((z) => !z.actiu).length;

  const visibles = useMemo(() => {
    const base = inactius ? totes : totes.filter((z) => z.actiu);
    if (!q) return base;
    // Amb cerca: zones que coincideixen i tots els seus avantpassats (per mantenir el context).
    const perId = new Map(base.map((z) => [z.id, z]));
    const mostra = new Set<number | null | undefined>();
    for (const z of base) {
      if (!normalitza(`${z.nom} ${z.codi} ${z.adreca ?? ''}`).includes(q)) continue;
      let actual: ZonaAdminDto | undefined = z;
      while (actual && !mostra.has(actual.id)) {
        mostra.add(actual.id);
        actual = actual.pareId != null ? perId.get(actual.pareId) : undefined;
      }
    }
    return base.filter((z) => mostra.has(z.id));
  }, [totes, inactius, q]);

  const nodes = arbreZones(visibles);
  // Amaga els descendents de les zones plegades (si no s'està cercant).
  const amagades = new Set<number>();
  if (!q) for (const id of plegades) descendents(visibles, id).forEach((d) => amagades.add(d));
  const files = nodes.filter((n) => n.zona.id == null || !amagades.has(n.zona.id));

  const commuta = (id: number) =>
    setPlegades((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const nova = (pareId?: number | null) =>
    setEdita({ codi: '', nom: '', tipus: 'CENTRE', actiu: true, pareId: pareId ?? null });

  return (
    <section className="pantalla" data-screen="W-28">
      <Toolbar titol={t('titols.zones')}>
        {potAlta ? (
          <button type="button" className="btn btn--dark btn--s" onClick={() => nova()}>
            <Icona nom="plus" />
            {t('zones.nova')}
          </button>
        ) : null}
      </Toolbar>
      <div className="content stack">
        <div className="admin-filtres sis-filtres">
          <Cerca
            className="sis-cerca"
            valor={text}
            onCanvia={setText}
            placeholder={t('sistema.zones.cerca')}
          />
          <button
            type="button"
            className="chip"
            aria-pressed={inactius}
            onClick={() => setInactius(!inactius)}
          >
            {t('sistema.zones.inactives', { count: nInactives })}
          </button>
        </div>
        <div className="panel">
          {zones.isError ? (
            <ErrorCarrega onReintenta={() => void zones.refetch()} />
          ) : zones.isLoading ? (
            <Buit icona="map">{t('comu.carregant')}</Buit>
          ) : totes.length === 0 ? (
            <Buit icona="map">{t('sistema.zones.buit')}</Buit>
          ) : files.length === 0 ? (
            <Buit icona="magnifier">
              {q
                ? t('sistema.zones.senseResultats', { q: text.trim() })
                : t('sistema.zones.totesInactives')}
            </Buit>
          ) : (
            <ul className="sis-arbre" role="tree" aria-label={t('titols.zones')}>
              {files.map(({ zona: z, nivell, fills }) => {
                const plegada = z.id != null && plegades.has(z.id) && !q;
                return (
                  <li
                    key={z.id ?? z.codi}
                    role="treeitem"
                    aria-level={nivell + 1}
                    aria-expanded={fills > 0 ? !plegada : undefined}
                    className={`sis-arbre__fila${z.actiu ? '' : ' is-inactiu'}`}
                    style={{ paddingLeft: 12 + nivell * 22 }}
                    data-zona={z.id}
                  >
                    {fills > 0 ? (
                      <button
                        type="button"
                        className="sis-arbre__plega"
                        aria-label={
                          plegada
                            ? t('sistema.zones.desplega', { nom: z.nom })
                            : t('sistema.zones.plega', { nom: z.nom })
                        }
                        onClick={() => z.id != null && commuta(z.id)}
                      >
                        <Icona nom={plegada ? 'chevron-right' : 'chevron-down'} />
                      </button>
                    ) : (
                      <span className="sis-arbre__plega" aria-hidden="true" />
                    )}
                    <button
                      type="button"
                      className="sis-arbre__nom"
                      disabled={!potModificar}
                      onClick={() => setEdita(z)}
                      aria-label={t('zones.editaZona', { nom: z.nom })}
                    >
                      <Icona nom="map" className="dim" />
                      <strong>{z.nom}</strong>
                      <span className="mono t-caption dim">{z.codi}</span>
                    </button>
                    <span className="badge badge--gray">{t(`zones.tipusZona.${z.tipus}`)}</span>
                    {!z.actiu ? (
                      <span className="badge badge--incident">{t('comu.inactiu')}</span>
                    ) : null}
                    <span className="sis-arbre__adreca dim truncate">{z.adreca}</span>
                    {fills > 0 ? (
                      <span className="t-caption dim nowrap">
                        {t('sistema.zones.nFills', { count: fills })}
                      </span>
                    ) : null}
                    {potAlta ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--xs sis-arbre__afegeix"
                        aria-label={t('sistema.zones.afegeixDins', { nom: z.nom })}
                        title={t('sistema.zones.afegeixDins', { nom: z.nom })}
                        onClick={() => nova(z.id)}
                      >
                        <Icona nom="plus" />
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      {edita ? <EditaZona inicial={edita} zones={totes} onTanca={() => setEdita(null)} /> : null}
    </section>
  );
}
